/**
 * Converts a given string to a URL-friendly "handle".
 *
 * This function transforms the input string by:
 * - Converting all characters to lowercase.
 * - Removing all non-alphanumeric characters except spaces and hyphens.
 * - Trimming spaces from the start and end of the string.
 * - Replacing spaces and one or more subsequent hyphens with a single hyphen.
 * - Removing leading and trailing hyphens.
 *
 * @param {string} input - The string to be converted to a handle.
 * @returns {(string|boolean)} - The handleized string or `false` if the input is falsy.
 */
export function handleize(input) {
  if (!input || typeof input !== 'string') return false
  return input
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/[\s-]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * Converts string in snake case to camel case.
 *
 * @param {string} string - String to convert.
 * @returns {string} - Converted string in camel case.
 */
export function snakeToCamel(string) {
  return string.replace(/(_\w)/g, (matches) => matches[1].toUpperCase())
}

/**
 * Creates a debounced function that delays invoking `func` until after `wait` milliseconds
 * have elapsed since the last time the debounced function was invoked.
 *
 * @function
 * @param {Function} func - The function to debounce.
 * @param {number} wait - The number of milliseconds to delay.
 * @param {boolean} immediate - If `true`, trigger the function on the leading edge, instead of the trailing.
 * @returns {Function} - A new debounced function.
 */
export function debounce(func, wait, immediate) {
  let timeout
  return function () {
    let context = this
    let args = arguments

    const callNow = immediate && !timeout

    clearTimeout(timeout)

    timeout = setTimeout(function () {
      timeout = null
      if (!immediate) {
        func.apply(context, args)
      }
    }, wait)
    if (callNow) func.apply(context, args)
  }
}

/**
 * Formats a number representing cents into a money string based on the provided format.
 *
 * @function
 * @param {(string | number)} cents - The amount of money in cents. If a string is provided, it will attempt to clean it.
 * @param {string} [format] - The format to output the money string in. If not provided, it will use the global money format.
 * @returns {string} - The formatted money string.
 * @example
 * // Returns "$19.99"
 * formatMoney(1999, "{{amount}}");
 * @example
 * // Returns "19.99"
 * formatMoney(1999, "{{amount_no_decimals}}");
 */
export function formatMoney(cents, format) {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '')
  }

  const defaultTo = function (value, defaultValue) {
    return value == null || value !== value ? defaultValue : value
  }

  let value = ''
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/
  const formatString = format || __THEME_GLOBALS__.moneyFormat

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultTo(precision, 2)
    thousands = defaultTo(thousands, ',')
    decimal = defaultTo(decimal, '.')

    if (isNaN(number) || number == null) {
      return 0
    }

    number = (number / 100.0).toFixed(precision)

    const parts = number.split('.')
    const dollarsAmount = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      '$1' + thousands
    )
    const centsAmount = decimal + parts[1]

    return dollarsAmount + centsAmount
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2)
      break
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0)
      break
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',')
      break
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',')
      break
  }

  return formatString.replace(placeholderRegex, value).replace('.00', '')
}

/**
 * Converts an HTML string to a DOM node.
 *
 * @function
 * @param {string} html - The HTML string to be converted.
 * @returns {ChildNode} - The first child node of the parsed HTML string's body.
 * @example
 * const node = htmlStringToNode('<div>Hello, World!</div>');
 * console.log(node); // Outputs: <div>Hello, World!</div>
 */
export function htmlStringToNode(html) {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  return doc.body.firstChild
}

/**
 * Strips HTML tags from a given HTML string and returns plain text.
 * This function uses the DOMParser API to parse the HTML string into a DOM Document,
 * and then retrieves the text content, effectively removing all HTML tags.
 *
 * @param {string} htmlString - The HTML string to strip tags from.
 * @returns {string} The plain text content without any HTML tags.
 */
export function stripHtml(htmlString) {
  const parser = new DOMParser()
  const doc = parser.parseFromString(htmlString, 'text/html')
  return doc.body.textContent || ''
}

/**
 * Determines the current viewport category based on the window's inner width.
 *
 * @function
 * @returns {string} - The current viewport category, which can be 'mobile', 'tablet', or 'desktop'.
 * @example
 * const viewport = getViewport();
 * console.log(viewport); // Outputs: 'mobile', 'tablet', or 'desktop'
 */
export function getViewport() {
  const width = window.innerWidth
  if (width <= 400) {
    return 'mobile-small'
  } else if (width > 400 && width <= 480) {
    return 'mobile'
  } else if (width > 480 && width <= 768) {
    return 'tablet-small'
  } else if (width > 768 && width <= 1024) {
    return 'tablet'
  } else if (width > 1024 && width <= 1200) {
    return 'desktop-small'
  } else {
    return 'desktop'
  }
}

/**
 * Adds one or more product variants to the Shopify cart.
 *
 * @async
 * @function
 * @param {Array|Object} items - An array of objects or a single object representing the items to add to the cart.
 * Each object should have an `id` property (variant ID) and a `quantity` property.
 * @param {boolean} [openCart=true] - Whether to trigger the cart to open after adding items.
 * @throws Will throw an error if the fetch operation encounters a problem.
 * @example
 * addToCart({ id: '1234567890', quantity: 1 });
 * addToCart([{ id: '1234567890', quantity: 1 }, { id: '0987654321', quantity: 2 }]);
 */
export async function addToCart(items, openCart = true) {
  if (!Array.isArray(items)) {
    items = [items]
  }
  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items }),
    })
    if (!response.ok) {
      throw new Error('Network response was not ok')
    }
    const responseJson = await response.json()

    if (openCart) {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }))
      document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }))
    }
    return responseJson
  } catch (error) {
    console.error(
      'There was a problem with the fetch operation:',
      error.message
    )
  }
}

/**
 * Update the cart's line item quantities
 *
 * @async
 * @function
 * @param {Array|Object} items - An array of objects or a single object representing the items to update
 * Each object should have an `id` property (variant ID or line item key) and a `quantity` property.
 * @param {boolean} [openCart=true] Whether to trigger the cart to open after updating.
 * @throws Will throw an error if the fetch operation encounters a problem.
 * @example
 * removeFromCart({ id: '1234567890', quantity: 1 });
 * removeFromCart([{ id: '1234567890', quantity: 1 }, { id: '0987654321', quantity: 2 }]);
 */
export async function updateCartQuantity(items, openCart = true) {
  if (!Array.isArray(items)) {
    items = [items]
  }

  try {
    const updates = {}
    for (const item of items) {
      updates[item.id] = item.quantity
    }

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ updates }),
    })

    if (!response.ok) {
      throw new Error('Network response was not ok')
    }

    const responseData = await response.json()

    if (openCart) {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }))
      document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }))
    }
  } catch (error) {
    console.error(
      'There was a problem with the fetch operation:',
      error.message
    )
  }
}

/**
 * Checks whether a value represents a valid number.
 * @example
 * validateNumber(NaN)       // returns false
 * validateNumber(undefined) // returns false
 * validateNumber(1)         // returns true
 * validateNumber(1.1)       // returns true
 * validateNumber('1')       // returns true
 * validateNumber('1.1')     // returns true
 * validateNumber('$1.00')   // returns false (preformatted price needs a different check)
 *
 * @param {*} value - The value to check.
 * @returns {boolean} - True if value is a valid number, false otherwise.
 */
export const validateNumber = (value) => Number.isFinite(parseFloat(value))

/**
 * Converts number representing a price in minor unit (e.g. cents) to major unit (e.g. dollars).
 *
 * @param {number|string} priceInMinorUnit - The price in minor unit.
 * @returns {number|string|false} - The price in major unit or false.
 */
export function minorToMajorUnit(priceInMinorUnit) {
  if (!validateNumber(priceInMinorUnit)) return false
  return (priceInMinorUnit / 100).toFixed(2)
}

/**
 * Simple formatter to convert price to string.
 *
 * @param {number|string} priceInMinorUnit - The price in minor unit.
 * @param {string} [currencyUnit='$'] - The currency symbol.
 * @returns {string} - Formatted price string.
 */
export function formatPrice(priceInMinorUnit, currencyUnit = '$') {
  const priceInMajorUnit = minorToMajorUnit(priceInMinorUnit)
  if (!priceInMajorUnit) return ''
  return `${currencyUnit}${priceInMajorUnit}`
}

/**
 * Recursively converts all object keys from snake case to camel case.
 * This function handles nested objects and arrays, applying the conversion
 * to all string keys within the object structure.
 *
 * @param {Object|Array} obj The object or array to convert keys for. If the input is neither
 * an object nor an array, the input is returned as is.
 * @returns {Object|Array} A new object or array with all keys converted to camel case. The
 * structure of the input is preserved, and nested objects or arrays are
 * recursively processed.
 * @example
 * // returns { firstName: "John", lastName: "Doe", addressInfo: { streetAddress: "123 Elm St", city: "Somewhere", postalCode: "12345" } }
 * convertKeysToCamelCase({ first_name: "John", last_name: "Doe", address_info: { street_address: "123 Elm St", city: "Somewhere", postal_code: "12345" } });
 */
export function convertKeysToCamelCase(obj) {
  if (typeof obj !== 'object' || obj === null) {
    return obj // Return the value if it's not an object
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => convertKeysToCamelCase(item)) // Handle arrays
  }

  return Object.keys(obj).reduce((acc, key) => {
    const camelKey = snakeToCamel(key)
    acc[camelKey] = convertKeysToCamelCase(obj[key]) // Recursively apply to nested objects
    return acc
  }, {})
}

/**
 * Checks if a given string represents a valid stringified array.
 *
 * @param {string} string - The string to check.
 * @returns {boolean} - Returns true if the string is a valid stringified array, otherwise false.
 */
export function isStringifiedArray(string) {
  if (typeof string !== 'string') return false
  if (string.startsWith('[') && string.endsWith(']')) {
    try {
      const potentialArray = JSON.parse(string)
      return Array.isArray(potentialArray)
    } catch (error) {
      return false
    }
  }
  return false
}

/**
 * Interpolates a template string with provided replacements.
 *
 * @param {string} template - The string template with placeholders.
 * @param {Object} replacements - An object containing key-value pairs for placeholders and their replacements.
 * @returns {string} The interpolated string.
 *
 * @example
 *   const messageTemplate = 'Hello, {name}! You have {count} messages.';
 *   const values = {
 *     name: 'Alice',
 *     count: 5
 *   }
 *   const message = interpolate(messageTemplate, values);
 *   console.log(message); // "Hello, Alice! You have 5 messages."
 */
export function interpolate(template, replacements) {
  return template.replace(/\{(\w+)\}/g, (_, placeholder) => {
    return replacements[placeholder] || _
  })
}

/**
 * Regular expression to match a size fragment in a Shopify image URL.
 * @type {RegExp}
 */
const sizeFragmentRegex = /\_(?:\d+x\d+|\d+x)\./i

/**
 * Ensures a Shopify image URL is using a specific size.
 *
 * @param {string} imageUrl - The Shopify image URL.
 * @param {string} size - The desired size for the image (e.g., '100x100', '500x', etc.).
 * @returns {string} The updated image URL with the specified size.
 *
 * @example
 * // Returns 'https://example.com/image_100x100.jpg'
 * ensureImageSize('https://example.com/image.jpg', '100x100');
 *
 * @example
 * // Returns 'https://example.com/image_500x.jpg'
 * ensureImageSize('https://example.com/image_100x100.jpg', '500x');
 */
export function ensureImageSize(imageUrl, size) {
  if (!imageUrl) return ''
  if (hasSizeFragment(imageUrl)) {
    return imageUrl.replace(sizeFragmentRegex, `_${size}x.`)
  } else {
    const fileExtensionIndex = imageUrl.lastIndexOf('.')
    const fileExtension = imageUrl.slice(fileExtensionIndex)
    const baseUrl = imageUrl.slice(0, fileExtensionIndex)
    return `${baseUrl}_${size}x${fileExtension}`
  }
}

/**
 * Checks if a Shopify image URL contains a size fragment.
 *
 * @param {string} imageUrl - The Shopify image URL to check.
 * @returns {boolean} Returns true if the URL contains a size fragment, otherwise false.
 *
 * @example
 * // Returns true
 * hasSizeFragment('https://example.com/image_100x100.jpg');
 *
 * @example
 * // Returns true
 * hasSizeFragment('https://example.com/image_100x.jpg');
 *
 * @example
 * // Returns false
 * hasSizeFragment('https://example.com/image.jpg');
 */
export function hasSizeFragment(imageUrl) {
  return sizeFragmentRegex.test(imageUrl)
}

/**
 * Paginates an array into subarrays of specified size, with an optional extra item on the first page.
 *
 * @param {Array} array - The array to be paginated.
 * @param {number} itemsPerPage - The number of items per page.
 * @param {boolean} [extraItemOnFirstPage=false] - Whether to include an extra item on the first page.
 * @returns {Array} An array of paginated subarrays.
 *
 * @example
 * const array = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
 * const itemsPerPage = 3;
 * const extraItemOnFirstPage = true;
 *
 * const result = paginateArray(array, itemsPerPage, extraItemOnFirstPage);
 * console.log(result);
 * // Output: [[1, 2, 3, 4], [5, 6, 7], [8, 9, 10]]
 */
export function paginateArray(array, itemsPerPage, extraItemOnFirstPage) {
  const paginatedArray = []
  let startIndex = 0

  if (extraItemOnFirstPage) {
    paginatedArray.push(array.slice(0, itemsPerPage + 1))
    startIndex = itemsPerPage + 1
  }

  for (let i = startIndex; i < array.length; i += itemsPerPage) {
    paginatedArray.push(array.slice(i, i + itemsPerPage))
  }

  return paginatedArray
}
